// frontend/src/components/CobrancaModal/CobrancaModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Drawer, Button, message, Typography, Alert } from 'antd';
import { WhatsAppOutlined, QrcodeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import logoVertical from '../../assets/images/logo_vertical.png';
import { getCobrancaLink, getCobrancaLinkProjecao } from '../../services/transactionService';
import { buildWhatsAppUrl } from '../../utils/whatsappPhone';
import './CobrancaModal.css';

const { Text, Title } = Typography;

const COLORS = {
    background: '#F5F0E6',
    textMain: '#1F4F4F',
    accent: '#50B0A0',
    gold: '#B89040',
    white: '#FFFFFF'
};

const CobrancaModal = ({ visible, onClose, transacao, userPix, userBanco }) => {
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', text: string }
    const [generatedWhatsAppUrl, setGeneratedWhatsAppUrl] = useState(null);
    const [generatedCardUrl, setGeneratedCardUrl] = useState(null);

    useEffect(() => {
        if (visible) {
            setFeedback(null);
            setGeneratedWhatsAppUrl(null);
            setGeneratedCardUrl(null);
        }
    }, [visible]);

    if (!transacao) return null;

    const isProjecao = transacao.item_type?.startsWith('transacao_projetada') || String(transacao.id || '').startsWith('proj_');
    const transacaoId = !isProjecao && transacao.id != null ? Number(transacao.id) : null;
    const hasPix = Boolean(userPix && String(userPix).trim());

    const valorFormatado = parseFloat(transacao.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    let dataVencimento = '-';
    if (transacao.data_vencimento) {
        const dateObj = new Date(transacao.data_vencimento + 'T12:00:00');
        if (!isNaN(dateObj)) {
            dataVencimento = dateObj.toLocaleDateString('pt-BR');
        } else {
            dataVencimento = new Date(transacao.data_vencimento).toLocaleDateString('pt-BR');
        }
    }

    const copiarParaAreaTransferencia = async (texto) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(texto);
                return true;
            }
        } catch {
            // Fallback para iOS Safari e navegadores que bloqueiam clipboard após async
        }
        const textarea = document.createElement('textarea');
        textarea.value = texto;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
            const ok = document.execCommand('copy');
            document.body.removeChild(textarea);
            return ok;
        } catch {
            document.body.removeChild(textarea);
            return false;
        }
    };

    const handleAction = async () => {
        setFeedback(null);
        if (!isProjecao && (transacaoId == null || isNaN(transacaoId))) {
            setFeedback({ type: 'error', text: 'Esta transação não possui ID válido para gerar o link.' });
            return;
        }
        if (isProjecao && (!transacao.recorrencia_id || !(transacao.data_original_ocorrencia || transacao.data_vencimento))) {
            setFeedback({ type: 'error', text: 'Dados da projeção incompletos para gerar o link.' });
            return;
        }
        setLoading(true);
        message.loading('Gerando link...', 0);

        try {
            const data = isProjecao
                ? await getCobrancaLinkProjecao(transacao.recorrencia_id, transacao.data_original_ocorrencia || transacao.data_vencimento)
                : await getCobrancaLink(transacaoId);
            const url = data?.url;
            if (!url) {
                throw new Error('Resposta da API sem URL.');
            }

            const nomeDevedor = transacao.nome_devedor || 'Cliente';
            const infoPixTexto = hasPix
                ? `\n\n🔑 *Chave Pix:*\n${userPix}\n${userBanco ? `🏦 *Banco:* ${userBanco}` : ''}`
                : '';

            const textoMensagem = `*Lembrete de pagamento - ${valorFormatado}*

${nomeDevedor} - ${transacao.descricao} - Vencimento: ${dataVencimento}${infoPixTexto}

*Ver card com os detalhes:* ${url}`;

            const whatsappUrl = buildWhatsAppUrl(
                textoMensagem,
                transacao.cliente_telefone || transacao.cliente?.telefone,
            );

            copiarParaAreaTransferencia(textoMensagem).catch(() => {});
            message.destroy();
            message.success('Link copiado!', 4);
            setFeedback({
                type: 'success',
                text: hasPix
                    ? 'Link gerado. Clique no botão abaixo para abrir o WhatsApp.'
                    : 'Link gerado sem chave Pix. Cadastre o Pix nas Configurações para incluir na próxima mensagem.',
            });
            setGeneratedWhatsAppUrl(whatsappUrl);
            setGeneratedCardUrl(url);
        } catch (error) {
            console.error('Cobrança link error:', error);
            message.destroy();
            const data = error.response?.data || {};
            const msg = data.error || data.detail || (typeof data === 'string' ? data : null) || error.message || 'Erro ao gerar link da cobrança. Verifique se o backend está rodando e se esta transação é uma cobrança.';
            message.error(msg);
            setFeedback({ type: 'error', text: msg });
        } finally {
            setLoading(false);
        }
    };

    const body = (
            <div className="cobranca-modal-container">

                {/* --- CARD VISUAL --- */}
                <div
                    style={{
                        width: '100%',
                        backgroundColor: COLORS.background,
                        padding: '32px 24px',
                        borderRadius: '16px',
                        border: `1px solid ${COLORS.accent}40`,
                        boxShadow: '0 10px 25px rgba(31, 79, 79, 0.1)',
                        color: COLORS.textMain,
                        fontFamily: "'Poppins', sans-serif",
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8px', background: COLORS.accent }}></div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
                        <img
                            src={logoVertical}
                            alt="Saldo Ativo"
                            style={{
                                height: '140px',
                                marginBottom: '12px',
                                objectFit: 'contain'
                            }}
                        />
                        <span style={{
                            fontSize: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            color: COLORS.gold,
                            fontWeight: 'bold',
                            marginTop: '8px'
                        }}>Lembrete de Pagamento</span>
                    </div>

                    <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                        <Text style={{
                            color: COLORS.accent,
                            fontSize: '12px',
                            fontWeight: 'bold',
                            letterSpacing: '3px',
                            display: 'block',
                            marginBottom: '4px'
                        }}>VALOR TOTAL</Text>
                        <Title level={1} style={{ color: COLORS.textMain, margin: 0, fontSize: '42px' }}>{valorFormatado}</Title>
                    </div>

                    <div style={{ background: COLORS.white, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, borderBottom: '1px dashed #eee', paddingBottom: 12 }}>
                            <Text style={{ color: 'var(--color-text-secondary, #5A6A6A)' }}>Pagador</Text>
                            <Text style={{ color: COLORS.textMain, fontWeight: 'bold', fontSize: '15px' }}>{transacao.nome_devedor || 'Cliente'}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, borderBottom: '1px dashed #eee', paddingBottom: 12 }}>
                            <Text style={{ color: 'var(--color-text-secondary, #5A6A6A)' }}>Referente a</Text>
                            <Text style={{ color: COLORS.textMain, fontWeight: 'bold', fontSize: '15px', textAlign: 'right', maxWidth: '60%' }}>{transacao.descricao}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: hasPix ? 12 : 0, borderBottom: hasPix ? '1px dashed #eee' : 'none', paddingBottom: hasPix ? 12 : 0 }}>
                            <Text style={{ color: 'var(--color-text-secondary, #5A6A6A)' }}>Vencimento</Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: COLORS.gold }}></span>
                                <Text style={{ color: COLORS.textMain, fontWeight: 'bold', fontSize: '15px' }}>{dataVencimento}</Text>
                            </div>
                        </div>
                        {hasPix && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: userBanco ? 12 : 0, borderBottom: userBanco ? '1px dashed #eee' : 'none', paddingBottom: userBanco ? 12 : 0 }}>
                                    <Text style={{ color: 'var(--color-text-secondary, #5A6A6A)' }}>Pix</Text>
                                    <Text style={{ color: COLORS.textMain, fontWeight: 'bold', fontSize: '14px', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{userPix}</Text>
                                </div>
                                {userBanco ? (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text style={{ color: 'var(--color-text-secondary, #5A6A6A)' }}>Banco</Text>
                                        <Text style={{ color: COLORS.textMain, fontWeight: 'bold', fontSize: '15px' }}>{userBanco}</Text>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>

                    <div style={{ marginTop: 24, textAlign: 'center' }}>
                        <Text style={{ color: COLORS.textMain, fontSize: '10px', opacity: 0.6 }}>Gestão Financeira via Saldo Ativo</Text>
                    </div>
                </div>

                <div style={{ width: '100%', textAlign: 'center', marginTop: 16 }}>
                    {!hasPix && (
                        <Alert
                            type="warning"
                            showIcon
                            icon={<QrcodeOutlined />}
                            style={{ marginBottom: 12, textAlign: 'left' }}
                            message="Chave Pix não cadastrada"
                            description={
                                <span>
                                    A mensagem será enviada sem a chave Pix.{' '}
                                    <Link to="/configuracoes" onClick={onClose}>
                                        Cadastrar Pix nas Configurações
                                    </Link>
                                </span>
                            }
                        />
                    )}
                    {feedback && (
                        <div style={{
                            marginBottom: 12,
                            padding: 8,
                            borderRadius: 8,
                            background: feedback.type === 'error' ? '#fff2f0' : '#f6ffed',
                            color: feedback.type === 'error' ? '#D64545' : '#2F9E6F',
                            fontSize: 13
                        }}>
                            {feedback.text}
                        </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        O texto com o link do card será copiado.<br />
                        O destinatário verá o preview do card na mensagem.
                    </Text>

                    {hasPix && (
                        <Button
                            icon={<QrcodeOutlined />}
                            size="large"
                            block
                            onClick={async () => {
                                const ok = await copiarParaAreaTransferencia(String(userPix).trim());
                                if (ok) {
                                    message.success('Chave Pix copiada!');
                                } else {
                                    message.error('Não foi possível copiar a chave Pix.');
                                }
                            }}
                            style={{ marginTop: 12 }}
                        >
                            Copiar chave Pix
                        </Button>
                    )}

                    {generatedCardUrl && (
                        <Button
                            size="large"
                            block
                            onClick={async () => {
                                const ok = await copiarParaAreaTransferencia(generatedCardUrl);
                                if (ok) {
                                    message.success('Link do card copiado!');
                                } else {
                                    message.error('Não foi possível copiar o link.');
                                }
                            }}
                            style={{ marginTop: 8 }}
                        >
                            Copiar link do card
                        </Button>
                    )}

                    {generatedWhatsAppUrl ? (
                        <a
                            href={generatedWhatsAppUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'block', marginTop: 12 }}
                        >
                            <Button
                                type="primary"
                                icon={<WhatsAppOutlined />}
                                size="large"
                                block
                                style={{
                                    backgroundColor: '#25D366',
                                    borderColor: '#25D366',
                                    height: '48px',
                                    fontWeight: 'bold',
                                    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)'
                                }}
                            >
                                Abrir WhatsApp
                            </Button>
                        </a>
                    ) : (
                        <Button
                            type="primary"
                            icon={<WhatsAppOutlined />}
                            size="large"
                            block
                            onClick={handleAction}
                            loading={loading}
                            style={{
                                backgroundColor: '#25D366',
                                borderColor: '#25D366',
                                height: '48px',
                                fontWeight: 'bold',
                                marginTop: '12px',
                                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)'
                            }}
                        >
                            Copiar e Abrir WhatsApp
                        </Button>
                    )}
                </div>
            </div>
    );

    if (isMobile) {
        return (
            <Drawer
                open={visible}
                onClose={onClose}
                placement="bottom"
                height="auto"
                title="Gerar Cobrança / Lembrete"
                className="cobranca-sheet"
                zIndex={1200}
                styles={{
                    body: {
                        paddingTop: 8,
                        paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                        maxHeight: '85dvh',
                        overflowY: 'auto',
                    },
                    content: { borderRadius: '16px 16px 0 0' },
                }}
            >
                {body}
            </Drawer>
        );
    }

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            title="Gerar Cobrança / Lembrete"
            width={450}
            centered
            style={{ top: 20 }}
            zIndex={1200}
        >
            {body}
        </Modal>
    );
};

export default CobrancaModal;
